import React, { useEffect, useRef } from 'react';

/**
 * A small ambient canvas behind the hero phone: three soft pools of the site
 * accent drifting on slow Lissajous paths, blurred by the compositor. It is
 * the only motion in the hero and it is deliberately quiet - no library, no
 * WebGL, no gradient the brand does not already own (handoff §2: native
 * materials). Rules it keeps:
 *
 *  - never on the critical path: the phone stays the LCP element, the canvas
 *    paints after mount and draws nothing until then (the CSS glow shows);
 *  - `prefers-reduced-motion` renders nothing at all, so the static CSS glow
 *    is what those visitors get;
 *  - it stops when the hero is off-screen or the tab is hidden - an ambient
 *    effect that keeps a phone's GPU awake behind a scrolled-past hero is a
 *    battery drain with no audience;
 *  - ~30 fps, device-pixel ratio capped at 2.
 */
const POOLS = [
  // colour, radius (fraction of the shorter side), orbit speeds, phase
  { rgb: '63, 191, 84', alpha: 0.34, r: 0.46, fx: 0.061, fy: 0.047, px: 0.0, py: 1.3 },
  { rgb: '111, 224, 131', alpha: 0.2, r: 0.32, fx: 0.043, fy: 0.071, px: 2.1, py: 0.4 },
  { rgb: '27, 94, 32', alpha: 0.42, r: 0.4, fx: 0.037, fy: 0.053, px: 4.0, py: 2.6 },
];

const FRAME_MS = 1000 / 30;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const HeroAmbient = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prefersReducedMotion()) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let raf = 0;
    let last = 0;
    let visible = true;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const size = () => {
      const box = canvas.parentElement ? canvas.parentElement.getBoundingClientRect() : null;
      width = Math.max(1, Math.round(box ? box.width : canvas.clientWidth));
      height = Math.max(1, Math.round(box ? box.height : canvas.clientHeight));
      dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (t) => {
      const s = t / 1000;
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';
      const base = Math.min(width, height);
      POOLS.forEach((p) => {
        const x = width * (0.5 + 0.28 * Math.sin(s * p.fx * Math.PI * 2 + p.px));
        const y = height * (0.42 + 0.22 * Math.cos(s * p.fy * Math.PI * 2 + p.py));
        const r = base * p.r;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(${p.rgb}, ${p.alpha})`);
        g.addColorStop(0.55, `rgba(${p.rgb}, ${p.alpha * 0.35})`);
        g.addColorStop(1, `rgba(${p.rgb}, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const tick = (t) => {
      raf = 0;
      if (!visible || document.hidden) return;
      if (t - last >= FRAME_MS) {
        last = t;
        draw(t);
      }
      raf = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (!raf && visible && !document.hidden) raf = window.requestAnimationFrame(tick);
    };
    const stop = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
    };

    size();
    draw(0);
    canvas.classList.add('hero-ambient--live');

    const onResize = () => {
      size();
      draw(performance.now());
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    let observer = null;
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting;
          if (visible) start();
          else stop();
        },
        { threshold: 0.05 },
      );
      observer.observe(canvas);
    } else {
      start();
    }

    return () => {
      stop();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      if (observer) observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="hero-ambient" aria-hidden="true" />;
};

export default HeroAmbient;
